/**
 * InvestmentReconciliationOverlay — reconciliation summary for investment statements.
 *
 * Declared via x-overlay: "investment_reconciliation" on the transactions
 * array schema node. Computes three investment-specific checks and renders
 * them as a summary bar above the activity table.
 *
 * Unlike bank statements (where begin + sum(txns) = end), investment
 * statements have market value changes that are implicit, so the simple
 * running balance approach does not apply. Instead we validate:
 *
 *  1. Share count reconciliation: begin_shares + net_txn_shares = end_shares
 *  2. Portfolio value reconciliation: sum(securities MV) + cash ~ end_balance
 *  3. Transaction arithmetic: qty x price ~ amount (for security txns)
 *
 * Props:
 *   parentData: object — statement data with begin_balance, end_balance
 *   rows: Array — transactions array
 *   securities: Array — top-level or statement-level securities
 *   cashPositions: Array — top-level or statement-level cash positions
 */

import { formatCurrency } from '../../../utils/formatters';

// Tolerances matching the backend (ai_ocr_scan_quality.py)
const SHARE_TOLERANCE = 0.001;
const MV_TOLERANCE = 0.01;
const TXN_ARITHMETIC_ABS = 0.01;
const TXN_ARITHMETIC_PCT = 0.01;
const HOLDINGS_RECON_PCT = 0.02;
const TXN_BALANCE_TOLERANCE = 0.01;

/**
 * Compute share count reconciliation per security.
 * Returns { passed, details: [{ security_name, beginning, net, ending, diff }] }
 */
export function computeShareCountReconciliation(rows, securities) {
  if (!securities || securities.length === 0) {
    return { passed: null, details: [] }; // N/A
  }

  // Build per-security net share deltas from transactions
  const txnShares = {};
  for (const txn of rows || []) {
    const secName = txn.security_name;
    const qty = txn.quantity;
    if (!secName || qty == null) continue;

    const tt = (txn.transaction_type || '').toUpperCase();
    const sign = tt === 'SELL' || tt === 'SALE' ? -1 : 1;

    txnShares[secName] = (txnShares[secName] || 0) + sign * parseFloat(qty);
  }

  const details = [];
  let allPassed = true;

  for (const sec of securities) {
    const secName = sec.security_name || '(unknown)';
    const ending = sec.quantity;
    const beginning = sec.quantity_beginning;
    if (ending == null || beginning == null) continue;

    const net = txnShares[secName] || 0;
    const computed = beginning + net;
    const diff = Math.abs(computed - ending);

    details.push({ security_name: secName, beginning, net, ending, diff });

    if (diff > SHARE_TOLERANCE) {
      allPassed = false;
    }
  }

  return { passed: details.length > 0 ? allPassed : null, details };
}

/**
 * Compute portfolio value reconciliation: sum(MV) + cash ~ end_balance.
 * Returns { passed, securitiesTotal, cashTotal, endBalance, diff }
 */
export function computePortfolioReconciliation(parentData, securities, cashPositions) {
  const endBalance = parseFloat(parentData?.end_balance) || 0;
  const hasHoldings = (securities && securities.length > 0) || (cashPositions && cashPositions.length > 0);

  if (!hasHoldings) {
    return { passed: null, securitiesTotal: 0, cashTotal: 0, endBalance, diff: 0 };
  }

  let securitiesTotal = 0;
  for (const sec of securities || []) {
    securitiesTotal += parseFloat(sec.total_market_value) || 0;
  }

  let cashTotal = 0;
  for (const cp of cashPositions || []) {
    cashTotal += parseFloat(cp.quantity) || 0;
  }

  const totalHoldings = securitiesTotal + cashTotal;
  const diff = Math.abs(totalHoldings - endBalance);
  const tolerance = Math.max(endBalance * HOLDINGS_RECON_PCT, 0.01);
  const passed = diff <= tolerance;

  return { passed, securitiesTotal, cashTotal, endBalance, diff, tolerance };
}

/**
 * Compute transaction arithmetic: qty x price ~ amount.
 * Returns { passed, failures: [{ description, qty, price, computed, amount, diff }] }
 */
export function computeTransactionArithmetic(rows) {
  const failures = [];
  let hasCheckableTxns = false;

  for (const txn of rows || []) {
    const qty = txn.quantity;
    const price = txn.price;
    const amount = txn.amount;
    if (qty == null || price == null || amount == null) continue;

    hasCheckableTxns = true;
    const computed = Math.round(qty * price * 100) / 100;
    const diff = Math.abs(Math.abs(computed) - Math.abs(amount));

    if (diff > TXN_ARITHMETIC_ABS && diff > Math.abs(amount) * TXN_ARITHMETIC_PCT) {
      failures.push({
        description: txn.description || '(unknown)',
        qty, price, computed, amount, diff,
      });
    }
  }

  return { passed: failures.length === 0, failures, hasCheckableTxns };
}

/**
 * Compute market value consistency: shares x price_per_share ~ total_market_value.
 * Returns { passed, failures: [{ security_name, shares, price, computed, mv, diff }] }
 */
export function computeMarketValueConsistency(securities) {
  const failures = [];

  for (const sec of securities || []) {
    const qty = sec.quantity;
    const mvPerShare = sec.market_value_per_share;
    const totalMv = sec.total_market_value;
    if (qty == null || mvPerShare == null || totalMv == null) continue;

    const computed = Math.round(qty * mvPerShare * 100) / 100;
    const diff = Math.abs(computed - totalMv);

    if (diff > MV_TOLERANCE) {
      failures.push({
        security_name: sec.security_name || '(unknown)',
        shares: qty, price: mvPerShare, computed, mv: totalMv, diff,
      });
    }
  }

  return { passed: failures.length === 0, failures };
}

/**
 * Compute transaction cash flow balance: begin + sum(txns) ≈ end.
 * Returns { passed, beginBalance, txnSum, endBalance, diff }
 * N/A when begin_balance is null (snapshot-only statements).
 * Marked informational — investment statements naturally have a gap
 * without an explicit UNREALIZED_GAIN_LOSS transaction.
 */
export function computeTransactionCashFlowBalance(parentData, rows) {
  const beginBalance = parentData?.begin_balance;
  const endBalance = parseFloat(parentData?.end_balance) || 0;

  if (beginBalance == null) {
    return { passed: null, beginBalance: 0, txnSum: 0, endBalance, diff: 0 };
  }

  const begin = parseFloat(beginBalance) || 0;
  let txnSum = 0;
  for (const txn of rows || []) {
    txnSum += parseFloat(txn.amount) || 0;
  }

  const computed = begin + txnSum;
  const diff = Math.abs(computed - endBalance);

  return {
    passed: diff <= TXN_BALANCE_TOLERANCE,
    beginBalance: begin,
    txnSum: Math.round(txnSum * 100) / 100,
    endBalance,
    diff: Math.round(diff * 100) / 100,
  };
}

/**
 * Render the investment reconciliation summary bar.
 */
export function InvestmentReconciliationSummary({ parentData, rows, securities, cashPositions }) {
  const shareRecon = computeShareCountReconciliation(rows, securities);
  const portfolioRecon = computePortfolioReconciliation(parentData, securities, cashPositions);
  const txnArithmetic = computeTransactionArithmetic(rows);
  const mvConsistency = computeMarketValueConsistency(securities);
  const txnBalance = computeTransactionCashFlowBalance(parentData, rows);

  const statusStyle = (passed) => ({
    color: passed === true ? 'var(--color-success, #16a34a)'
      : passed === false ? 'var(--color-error, #dc2626)'
      : 'var(--color-gray-500, #6b7280)',
    fontWeight: 600,
  });

  const statusIcon = (passed) => passed === true ? '✓' : passed === false ? '✗' : '—';

  return (
    <div style={{
      display: 'flex', gap: '16px', padding: '6px 8px',
      background: 'var(--color-gray-50, #f9fafb)',
      borderBottom: '1px solid var(--color-gray-200, #e5e7eb)',
      fontSize: 'var(--text-sm, 13px)',
      flexWrap: 'wrap',
      alignItems: 'center',
    }}>
      <span>
        End Balance: <strong>{formatCurrency(parentData?.end_balance || 0)}</strong>
      </span>
      <span style={{ ...statusStyle(shareRecon.passed), display: 'flex', alignItems: 'center', gap: '4px' }}>
        {statusIcon(shareRecon.passed)} Share Counts
        {!shareRecon.passed && ` (${shareRecon.details.filter(d => d.diff > SHARE_TOLERANCE).length} mismatch)`}
      </span>
      <span style={{ ...statusStyle(portfolioRecon.passed), display: 'flex', alignItems: 'center', gap: '4px' }}>
        {statusIcon(portfolioRecon.passed)} Portfolio Value
        {portfolioRecon.passed === false && ` (off ${formatCurrency(portfolioRecon.diff)})`}
      </span>
      <span style={{ ...statusStyle(txnArithmetic.passed), display: 'flex', alignItems: 'center', gap: '4px' }}>
        {statusIcon(txnArithmetic.passed)} Txn Arithmetic
        {!txnArithmetic.passed && ` (${txnArithmetic.failures.length} fail)`}
      </span>
      <span style={{ ...statusStyle(mvConsistency.passed), display: 'flex', alignItems: 'center', gap: '4px' }}>
        {statusIcon(mvConsistency.passed)} MV Consistency
        {!mvConsistency.passed && ` (${mvConsistency.failures.length} fail)`}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--color-gray-500)' }}>
        — Txn Balance <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>(info)</span>
      </span>
    </div>
  );
}

/**
 * Overlay registry entry for investment_reconciliation.
 */
export const investmentReconciliationOverlay = {
  id: 'investment_reconciliation',
  SummaryComponent: InvestmentReconciliationSummary,
  // No per-row running balance column — investments don't have a meaningful
  // running balance (market value changes are implicit).
  computeColumn: null,
  columnLabel: null,
};
